import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Alert,
  Badge,
  Button,
  Col,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { toast } from "react-toastify"
import ThemeModal from "components/Common/ThemeModal"
import { getVehicleStocks } from "api/stockApi"
import { importVehicleDriveImages } from "api/vehicleStockApi"

const getDriveLink = stock => stock?.drive_link || stock?.google_drive_link || ""

const hasImportedImages = stock =>
  Array.isArray(stock?.images) && stock.images.length > 0

const relationName = relation =>
  relation && relation.name ? relation.name : "-"

const getVehicleTitle = stock =>
  [
    relationName(stock?.brand),
    relationName(stock?.model),
    relationName(stock?.engine_type),
  ]
    .filter(value => value && value !== "-")
    .join(" ") || "-"

const getVehicleDetails = stock => ({
  stockId: stock.stock_id || stock.full_chassis || "-",
  fullChassis: stock.full_chassis || "-",
  color: relationName(stock?.exterior_color),
  mileage: stock.mileage ? `${Number(stock.mileage).toLocaleString()} km` : "-",
  auctionGrade: relationName(stock?.auction_grade),
})

const DRIVE_IMPORT_LIMIT = 30
const DRIVE_IMPORT_CHUNK_SIZE = 3
const DRIVE_IMPORT_CHUNK_DELAY_MS = 1200
const DRIVE_IMPORT_RETRY_DELAY_MS = 2500
const DRIVE_IMPORT_RETRY_LIMIT = 2
const DRIVE_IMPORT_IMAGE_MAX_KB = 100

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

const isAbortError = error =>
  error?.name === "CanceledError" ||
  error?.name === "AbortError" ||
  error?.code === "ERR_CANCELED"

const formatDuration = ms => {
  if (ms === null || ms === undefined || ms < 0) return "calculating..."
  if (ms === 0) return "0s"

  const totalSeconds = Math.max(1, Math.ceil(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes === 0) return `${seconds}s`

  return `${minutes}m ${seconds}s`
}

const STATUS_ICON = {
  queued: "bx-time",
  importing: "bx-loader-alt bx-spin",
  done: "bx-check-circle",
  failed: "bx-x-circle",
  canceled: "bx-minus-circle",
}

const STATUS_COLOR = {
  queued: "secondary",
  importing: "primary",
  done: "success",
  failed: "danger",
  canceled: "secondary",
}

const getImportProgress = row => {
  if (typeof row?.progress === "number") return row.progress

  if (
    row?.status === "done" ||
    row?.status === "failed" ||
    row?.status === "canceled"
  ) {
    return 100
  }

  if (row?.status === "importing") return 20

  return 0
}

const StatusIcon = ({ status }) => (
  <i
    className={`bx ${STATUS_ICON[status] || "bx-circle"} text-${STATUS_COLOR[status] || "secondary"} me-1`}
    style={{ fontSize: 16 }}
  />
)

const SummaryMetric = ({ label, value, icon, color = "primary", note }) => (
  <div className="border rounded bg-white p-3 h-100">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div>
        <div className="text-muted small text-uppercase">{label}</div>
        <div className="h4 mb-0">{value}</div>
      </div>
      <span className={`badge bg-soft-${color} text-${color} p-2`}>
        <i className={`${icon} font-size-18`} />
      </span>
    </div>
    {note ? <div className="text-muted small mt-2">{note}</div> : null}
  </div>
)

const BulkDriveImageImportModal = ({ isOpen, toggle, onImported }) => {
  const [stocks, setStocks] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importedCount, setImportedCount] = useState(0)
  const [importRows, setImportRows] = useState([])
  const [importStartedAt, setImportStartedAt] = useState(null)
  const [progressTick, setProgressTick] = useState(0)
  const [cancelRequested, setCancelRequested] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState({
    alreadyImported: true,
  })
  const cancelImportRef = useRef(false)
  const activeControllerRef = useRef(null)
  const importTableRef = useRef(null)

  const loadReadyDriveStocks = useCallback(async () => {
    setIsLoading(true)

    try {
      const res = await getVehicleStocks({
        paginate: false,
        isShowCount: false,
        vs_ready_stock: 1,
        relations: "brand,model,engineType,exteriorColor,auctionGrade",
      })
      const rows = res?.data?.vehicles ?? []
      const driveRows = rows.filter(stock => Boolean(getDriveLink(stock)))

      setStocks(driveRows)
      setSelectedIds(
        driveRows
          .filter(stock => !hasImportedImages(stock))
          .map(stock => stock.id)
      )
    } catch (error) {
      console.error("Failed to load Drive image import vehicles:", error)
      toast.error("Failed to load ready stock vehicles with Drive links.")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      setImportedCount(0)
      setImportRows([])
      setImportStartedAt(null)
      setCancelRequested(false)
      loadReadyDriveStocks()
    }
  }, [isOpen, loadReadyDriveStocks])

  useEffect(() => {
    if (!isImporting) return undefined

    const timer = setInterval(() => {
      setProgressTick(tick => tick + 1)
    }, 1000)

    return () => clearInterval(timer)
  }, [isImporting])

  const groups = useMemo(() => {
    const needsImport = stocks.filter(stock => !hasImportedImages(stock))
    const alreadyImported = stocks.filter(stock => hasImportedImages(stock))

    return [
      {
        key: "needsImport",
        title: "No Images Imported",
        color: "warning",
        help: "Ready stock with Drive links and no vehicle images in Laravel.",
        rows: needsImport,
      },
      {
        key: "alreadyImported",
        title: "Already Has Images",
        color: "success",
        help:
          "Selecting these will replace current Laravel images and delete old files.",
        rows: alreadyImported,
      },
    ]
  }, [stocks])

  const selectedStocks = useMemo(
    () => stocks.filter(stock => selectedIds.includes(stock.id)),
    [stocks, selectedIds]
  )

  const toggleStock = stockId => {
    setSelectedIds(prev =>
      prev.includes(stockId)
        ? prev.filter(id => id !== stockId)
        : [...prev, stockId]
    )
  }

  const selectGroup = rows => {
    const groupIds = rows.map(stock => stock.id)
    setSelectedIds(prev => [...new Set([...prev, ...groupIds])])
  }

  const clearGroup = rows => {
    const groupIds = rows.map(stock => stock.id)
    setSelectedIds(prev => prev.filter(id => !groupIds.includes(id)))
  }

  const selectAll = () => {
    setSelectedIds(stocks.map(stock => stock.id))
  }

  const clearAll = () => {
    setSelectedIds([])
  }

  const toggleGroupCollapse = groupKey => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }))
  }

  const updateImportRow = (stockId, updates) => {
    setImportRows(prev =>
      prev.map(row => (row.id === stockId ? { ...row, ...updates } : row))
    )
  }

  const cancelImport = () => {
    cancelImportRef.current = true
    setCancelRequested(true)
    activeControllerRef.current?.abort()
    setImportRows(prev =>
      prev.map(row =>
        row.status === "queued"
          ? { ...row, status: "canceled", message: "Canceled before import" }
          : row
      )
    )
  }

  const importProgress = useMemo(() => {
    const total = importRows.length
    const completedRows = importRows.filter(row =>
      ["done", "failed", "canceled"].includes(row.status)
    )
    const completed = completedRows.length
    const percent = total ? Math.round((completed / total) * 100) : 0
    const rowProgress = total
      ? Math.round(
          importRows.reduce((sum, row) => sum + getImportProgress(row), 0) /
            total
        )
      : 0
    const elapsedMs = importStartedAt ? Date.now() - importStartedAt : 0
    const avgMs = completed > 0 ? elapsedMs / completed : null
    const remainingMs = avgMs ? avgMs * (total - completed) : null
    const importedImages = importRows.reduce(
      (totalImages, row) => totalImages + (row.importedImages || 0),
      0
    )
    const current = importRows.find(row => row.status === "importing")

    return {
      total,
      completed,
      percent: Math.max(percent, rowProgress),
      elapsed: formatDuration(elapsedMs),
      remaining: formatDuration(remainingMs),
      importedImages,
      current,
    }
  }, [importRows, importStartedAt, progressTick])

  const selectedSummary = useMemo(() => {
    const selectedNeedsImport = groups[0]?.rows.filter(stock =>
      selectedIds.includes(stock.id)
    )
    const selectedReplacements = groups[1]?.rows.filter(stock =>
      selectedIds.includes(stock.id)
    )

    return {
      needsImport: selectedNeedsImport?.length ?? 0,
      replacements: selectedReplacements?.length ?? 0,
    }
  }, [groups, selectedIds])

  const operationSummary = useMemo(() => {
    const done = importRows.filter(row => row.status === "done").length
    const failed = importRows.filter(row => row.status === "failed").length
    const canceled = importRows.filter(row => row.status === "canceled").length

    return { done, failed, canceled }
  }, [importRows])

  const handleImport = async () => {
    if (!selectedStocks.length || isImporting) return

    setIsImporting(true)
    setImportedCount(0)
    setCancelRequested(false)
    setImportStartedAt(Date.now())
    setProgressTick(tick => tick + 1)
    cancelImportRef.current = false

    const queue = selectedStocks.map(stock => {
      const details = getVehicleDetails(stock)
      return {
        id: stock.id,
        title: getVehicleTitle(stock),
        stockId: details.stockId,
        fullChassis: details.fullChassis,
        color: details.color,
        mileage: details.mileage,
        auctionGrade: details.auctionGrade,
        status: "queued",
        message: "Waiting",
        importedImages: 0,
        totalImages: null,
        progress: 0,
      }
    })

    setImportRows(queue)

    const failed = []

    for (const stock of selectedStocks) {
      if (cancelImportRef.current) break

      let offset = 0
      let hasMore = true
      let replaceExisting = true
      let totalImages = null
      let importedImagesTotal = 0
      let retryCount = 0
      let vehicleFailed = false

      updateImportRow(stock.id, {
        status: "importing",
        message: "Starting Drive import...",
        progress: 5,
      })

      while (hasMore && !cancelImportRef.current) {
        const controller = new AbortController()
        activeControllerRef.current = controller

        try {
          updateImportRow(stock.id, {
            status: "importing",
            message: totalImages
              ? `Importing images ${offset + 1}-${Math.min(
                  offset + DRIVE_IMPORT_CHUNK_SIZE,
                  totalImages
                )} of ${totalImages}`
              : "Reading Drive folder...",
          })

          const res = await importVehicleDriveImages({
            vehicleId: stock.id,
            driveLink: getDriveLink(stock),
            limit: DRIVE_IMPORT_LIMIT,
            offset,
            chunkSize: DRIVE_IMPORT_CHUNK_SIZE,
            replaceExisting,
            maxKb: DRIVE_IMPORT_IMAGE_MAX_KB,
            signal: controller.signal,
          })

          if (!res?.success) {
            throw new Error(res?.message || "Import failed")
          }

          const importedImages = res?.data?.imported?.length ?? 0
          totalImages = res?.data?.total_files ?? totalImages ?? importedImages
          offset = res?.data?.next_offset ?? offset + DRIVE_IMPORT_CHUNK_SIZE
          hasMore = Boolean(res?.data?.has_more)
          replaceExisting = false
          retryCount = 0
          importedImagesTotal += importedImages

          const progress = totalImages
            ? Math.min(95, Math.round((offset / totalImages) * 100))
            : 60

          updateImportRow(stock.id, {
            status: "importing",
            message: hasMore
              ? `${importedImagesTotal} of ${totalImages} image(s) imported`
              : "Finishing vehicle import...",
            importedImages: importedImagesTotal,
            totalImages,
            progress,
          })

          if (hasMore) {
            await wait(DRIVE_IMPORT_CHUNK_DELAY_MS)
          }
        } catch (error) {
          if (isAbortError(error) || controller.signal.aborted) {
            updateImportRow(stock.id, {
              status: "canceled",
              message: "Canceled during import",
              progress: 100,
            })
            break
          }

          retryCount += 1

          if (retryCount <= DRIVE_IMPORT_RETRY_LIMIT) {
            updateImportRow(stock.id, {
              status: "importing",
              message: `Network or Google delay. Retrying ${retryCount}/${DRIVE_IMPORT_RETRY_LIMIT}...`,
            })
            await wait(DRIVE_IMPORT_RETRY_DELAY_MS * retryCount)
            continue
          }

          vehicleFailed = true
          console.error("Drive image import failed:", error)
          failed.push(stock.stock_id || stock.full_chassis || stock.id)
          updateImportRow(stock.id, {
            status: "failed",
            message:
              error?.response?.data?.message ||
              error?.message ||
              "Import failed",
            importedImages: importedImagesTotal,
            totalImages,
            progress: 100,
          })
          hasMore = false
        } finally {
          activeControllerRef.current = null
        }
      }

      if (!cancelImportRef.current && !vehicleFailed) {
        setImportedCount(count => count + 1)
        updateImportRow(stock.id, {
          status: "done",
          message: `${importedImagesTotal} image(s) imported`,
          importedImages: importedImagesTotal,
          totalImages,
          progress: 100,
        })
        await wait(DRIVE_IMPORT_CHUNK_DELAY_MS)
      }
    }

    setIsImporting(false)
    onImported?.()
    await loadReadyDriveStocks()

    if (cancelImportRef.current) {
      toast.warning("Drive image import canceled.")
      return
    }

    if (failed.length) {
      toast.error(`Failed to import ${failed.length} vehicle(s).`)
      return
    }

    toast.success("Selected Drive images imported successfully.")
  }

  const isImportDone =
    importRows.length > 0 &&
    importRows.every(row =>
      ["done", "failed", "canceled"].includes(row.status)
    )

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={() => {
        if (!isImporting) toggle()
      }}
      title="Bulk Drive Image Import"
      size="xl"
      modalBodyClass="bg-light"
      footer={
        <div className="d-flex justify-content-between w-100 gap-2 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <Badge color="primary" pill>
              {selectedIds.length} selected
            </Badge>
            {isImporting && (
              <span className="text-muted small">
                Imported {importedCount} of {selectedStocks.length}
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button
              color="light"
              type="button"
              onClick={toggle}
              disabled={isImporting}
            >
              {isImportDone ? "Close" : "Close"}
            </Button>
            {isImporting && (
              <Button color="danger" type="button" onClick={cancelImport}>
                <i className="bx bx-x-circle me-1" />
                Cancel Import
              </Button>
            )}
            {!isImportDone && (
              <Button
                color="success"
                type="button"
                onClick={handleImport}
                disabled={isLoading || isImporting || selectedIds.length === 0}
              >
                {isImporting ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="bx bx-import me-1" />
                )}
                {isImporting ? "Importing..." : "Import Selected"}
              </Button>
            )}
          </div>
        </div>
      }
    >
      {/* Header */}
      <div className="border rounded bg-white p-3 mb-3">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div>
            <h5 className="mb-1">
              <i className="bx bx-cloud-upload me-2 text-primary" />
              Google Drive Image Import
            </h5>
            <div className="text-muted">
              Ready-stock vehicles with public Drive links are imported in
              small batches for shared hosting stability.
              Imported images are compressed to {DRIVE_IMPORT_IMAGE_MAX_KB}KB
              before they are saved.
            </div>
          </div>
          <Badge color={isImporting ? "info" : "secondary"} pill>
            {isImporting ? "Import running" : "Ready"}
          </Badge>
        </div>
      </div>

      {/* Summary Metrics */}
      <Row className="g-3 mb-3">
        <Col md={3} sm={6}>
          <SummaryMetric
            label="Drive Vehicles"
            value={isLoading ? <Spinner size="sm" /> : stocks.length}
            icon="bx bx-car"
            color="info"
            note="Ready stock only"
          />
        </Col>
        <Col md={3} sm={6}>
          <SummaryMetric
            label="Selected"
            value={selectedIds.length}
            icon="bx bx-check-square"
            color="primary"
            note={`${selectedSummary.needsImport} new, ${selectedSummary.replacements} replace`}
          />
        </Col>
        <Col md={3} sm={6}>
          <SummaryMetric
            label="No Images"
            value={groups[0]?.rows.length ?? 0}
            icon="bx bx-image-add"
            color="warning"
            note="Selected by default"
          />
        </Col>
        <Col md={3} sm={6}>
          <SummaryMetric
            label="Has Images"
            value={groups[1]?.rows.length ?? 0}
            icon="bx bx-revision"
            color="success"
            note="Replace only if selected"
          />
        </Col>
      </Row>

      {/* Info */}
      <Alert color="info" className="mb-3">
        <div className="d-flex gap-2">
          <i className="bx bx-shield-quarter font-size-18 mt-1" />
          <div>
            <strong>Safe import mode:</strong> images are imported in small
            batches of {DRIVE_IMPORT_CHUNK_SIZE} so the process stays stable on
            slow internet. If Google Drive is slow, the app waits and retries
            before marking a vehicle as failed. Existing images are removed only
            after the new images are saved. Laravel also re-compresses imported
            Drive images to {DRIVE_IMPORT_IMAGE_MAX_KB}KB before saving.
          </div>
        </div>
      </Alert>

      {/* Import Operation - Active or Done */}
      {importRows.length > 0 && (
        <div className="border rounded p-3 mb-3 bg-white">
          {/* Operation Header */}
          <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
            <div className="d-flex align-items-center gap-2">
              {isImporting ? (
                <Spinner size="sm" color="primary" />
              ) : isImportDone ? (
                <i className="bx bx-check-circle text-success font-size-18" />
              ) : (
                <i className="bx bx-cloud-upload text-primary font-size-18" />
              )}
              <h6 className="mb-0">
                {isImporting
                  ? "Importing..."
                  : isImportDone
                  ? "Import Complete"
                  : "Import Operation"}
              </h6>
              <Badge color="primary" pill>
                {importProgress.completed}/{importProgress.total}
              </Badge>
            </div>
            <div className="text-muted small text-end">
              <span className="me-3">
                <i className="bx bx-time me-1" />
                {importProgress.elapsed}
              </span>
              <span>
                <i className="bx bx-image me-1" />
                {importProgress.importedImages} images
              </span>
            </div>
          </div>

          {/* Status Badges */}
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge color="success" pill>
              <i className="bx bx-check-circle me-1" />
              Done {operationSummary.done}
            </Badge>
            <Badge color="danger" pill>
              <i className="bx bx-x-circle me-1" />
              Failed {operationSummary.failed}
            </Badge>
            <Badge color="secondary" pill>
              <i className="bx bx-minus-circle me-1" />
              Canceled {operationSummary.canceled}
            </Badge>
            {!isImportDone && (
              <Badge color="light" pill>
                <i className="bx bx-time me-1" />
                Remaining {importProgress.total - importProgress.completed}
              </Badge>
            )}
          </div>

          {/* Overall Progress */}
          <Progress
            value={importProgress.percent}
            color={cancelRequested ? "warning" : isImportDone ? "success" : "primary"}
            className="mb-3"
            style={{ height: 8 }}
          >
            {importProgress.percent}%
          </Progress>

          {/* Current Vehicle Status */}
          {isImporting && importProgress.current && (
            <div
              className="p-3 rounded mb-3"
              style={{ background: "#f0f7ff", borderLeft: "3px solid #0d6efd" }}
            >
              <div className="d-flex align-items-start gap-3">
                <Spinner size="sm" color="primary" className="mt-1" />
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                    <div>
                      <div className="fw-semibold">
                        {importProgress.current.title}
                      </div>
                      <div className="d-flex flex-wrap gap-2 mt-1" style={{ fontSize: 12 }}>
                        <span className="text-muted">
                          <i className="bx bx-car me-1" />
                          {importProgress.current.stockId}
                        </span>
                        {importProgress.current.fullChassis && importProgress.current.fullChassis !== "-" && (
                          <span className="text-muted">
                            <i className="bx bx-hash me-1" />
                            {importProgress.current.fullChassis}
                          </span>
                        )}
                        {importProgress.current.color && importProgress.current.color !== "-" && (
                          <span className="text-muted">
                            <i className="bx bx-palette me-1" />
                            {importProgress.current.color}
                          </span>
                        )}
                        {importProgress.current.mileage && importProgress.current.mileage !== "-" && (
                          <span className="text-muted">
                            <i className="bx bx-speedometer me-1" />
                            {importProgress.current.mileage}
                          </span>
                        )}
                        {importProgress.current.auctionGrade && importProgress.current.auctionGrade !== "-" && (
                          <span className="text-muted">
                            <i className="bx bx-star me-1" />
                            Grade {importProgress.current.auctionGrade}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-end">
                      {importProgress.current.totalImages && (
                        <Badge color="primary" pill>
                          {importProgress.current.importedImages} / {importProgress.current.totalImages} images
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <Progress
                      value={getImportProgress(importProgress.current)}
                      color="primary"
                      animated
                      style={{ height: 6 }}
                    />
                  </div>
                  <div className="text-muted small mt-1">
                    {importProgress.current.message}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Import Table */}
          <div
            className="table-responsive"
            ref={importTableRef}
            style={{ maxHeight: 350, overflowY: "auto" }}
          >
            <Table size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 30 }}></th>
                  <th>Vehicle</th>
                  <th>Stock ID</th>
                  <th style={{ width: 200 }}>Status</th>
                  <th style={{ width: 100 }}>Progress</th>
                  <th style={{ width: 70 }}>Images</th>
                </tr>
              </thead>
              <tbody>
                {importRows.map(row => (
                  <tr
                    key={row.id}
                    className={
                      row.status === "importing"
                        ? "table-active"
                        : row.status === "done"
                        ? "table-success bg-opacity-10"
                        : row.status === "failed"
                        ? "table-danger bg-opacity-10"
                        : ""
                    }
                  >
                    <td>
                      <StatusIcon status={row.status} />
                    </td>
                    <td>
                      <div className="fw-medium small">{row.title}</div>
                    </td>
                    <td className="text-muted small">{row.stockId}</td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted">{row.message}</small>
                      </div>
                    </td>
                    <td>
                      <Progress
                        value={getImportProgress(row)}
                        color={STATUS_COLOR[row.status]}
                        animated={row.status === "importing"}
                        striped={row.status === "importing"}
                        style={{ height: 6 }}
                      />
                    </td>
                    <td className="text-center">
                      <small className="fw-medium">
                        {row.importedImages || 0}
                        {row.totalImages ? `/${row.totalImages}` : ""}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* Vehicle Queue - Only show when not importing */}
      {!isImporting && (
        <>
          <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
            <div>
              <h6 className="mb-1">Vehicle Queue</h6>
              <div className="text-muted small">
                {isLoading
                  ? "Loading vehicles..."
                  : `${stocks.length} ready-stock vehicle(s) with Drive links`}
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                color="primary"
                outline
                onClick={selectAll}
                disabled={isLoading || isImporting}
              >
                Select All
              </Button>
              <Button
                size="sm"
                color="light"
                onClick={clearAll}
                disabled={isLoading || isImporting}
              >
                Clear All
              </Button>
              <Button
                size="sm"
                color="light"
                onClick={loadReadyDriveStocks}
                disabled={isLoading || isImporting}
              >
                {isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="bx bx-revision" />
                )}
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            groups.map(group => (
              <div className="border rounded mb-3 bg-white" key={group.key}>
                <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap border-bottom p-3">
                  <div>
                    <h6 className="mb-1">
                      <i
                        className={`${
                          group.key === "needsImport"
                            ? "bx bx-image-add"
                            : "bx bx-revision"
                        } me-1 text-${group.color}`}
                      />
                      {group.title}{" "}
                      <Badge color={group.color} pill>
                        {group.rows.length}
                      </Badge>
                      {group.key === "alreadyImported" &&
                        selectedSummary.replacements > 0 && (
                          <Badge color="primary" pill className="ms-1">
                            {selectedSummary.replacements} selected
                          </Badge>
                        )}
                    </h6>
                    <div className="text-muted small">{group.help}</div>
                  </div>
                  <div className="d-flex gap-2">
                    {group.key === "alreadyImported" ? (
                      <Button
                        size="sm"
                        color="light"
                        onClick={() => toggleGroupCollapse(group.key)}
                        disabled={!group.rows.length || isImporting}
                      >
                        <i
                          className={`bx ${
                            collapsedGroups[group.key] ? "bx-chevron-down" : "bx-chevron-up"
                          } me-1`}
                        />
                        {collapsedGroups[group.key] ? "Show" : "Hide"}
                      </Button>
                    ) : null}
                    {group.key === "needsImport" ? (
                      <>
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          onClick={() => selectGroup(group.rows)}
                          disabled={!group.rows.length || isImporting}
                        >
                          Select Group
                        </Button>
                        <Button
                          size="sm"
                          color="light"
                          onClick={() => clearGroup(group.rows)}
                          disabled={!group.rows.length || isImporting}
                        >
                          Clear Group
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>

                {group.rows.length && !collapsedGroups[group.key] ? (
                  <div
                    className="table-responsive"
                    style={{ maxHeight: 400, overflowY: "auto" }}
                  >
                    <Table className="mb-0 align-middle table-hover">
                      <thead>
                        <tr>
                          <th style={{ width: 120 }}></th>
                          <th>Vehicle</th>
                          <th>Stock ID</th>
                          <th>Images</th>
                          <th>Drive</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map(stock => (
                          <tr key={stock.id}>
                            <td>
                              <Button
                                size="sm"
                                color={
                                  selectedIds.includes(stock.id)
                                    ? "success"
                                    : group.key === "needsImport"
                                    ? "primary"
                                    : "warning"
                                }
                                outline={!selectedIds.includes(stock.id)}
                                onClick={() => toggleStock(stock.id)}
                                disabled={isImporting}
                                title={
                                  selectedIds.includes(stock.id)
                                    ? "Remove from import selection"
                                    : group.key === "needsImport"
                                    ? "Select for image import"
                                    : "Select for image replacement"
                                }
                              >
                                <i
                                  className={`bx ${
                                    selectedIds.includes(stock.id)
                                      ? "bx-check"
                                      : group.key === "needsImport"
                                      ? "bx-plus"
                                      : "bx-revision"
                                  } me-1`}
                                />
                                {selectedIds.includes(stock.id)
                                  ? "Selected"
                                  : group.key === "needsImport"
                                  ? "Add"
                                  : "Replace"}
                              </Button>
                            </td>
                            <td>
                              <div className="fw-medium">
                                {getVehicleTitle(stock)}
                              </div>
                              <div className="text-muted small">
                                {group.key === "needsImport"
                                  ? "New image import"
                                  : "Will replace existing images"}
                              </div>
                            </td>
                            <td>
                              <span className="fw-medium">
                                {stock.stock_id || stock.full_chassis || "-"}
                              </span>
                            </td>
                            <td>
                              <Badge
                                color={
                                  hasImportedImages(stock) ? "success" : "warning"
                                }
                                pill
                              >
                                {Array.isArray(stock.images)
                                  ? stock.images.length
                                  : 0}
                              </Badge>
                            </td>
                            <td>
                              <a
                                href={getDriveLink(stock)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-soft-primary py-0 px-2"
                              >
                                <i className="bx bx-link-external me-1" />
                                Drive
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : group.rows.length ? (
                  <div className="text-center text-muted py-3">
                    {group.rows.length} vehicle(s) hidden.
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    No vehicles in this group.
                  </div>
                )}
              </div>
            ))
          )}
        </>
      )}
    </ThemeModal>
  )
}

export default BulkDriveImageImportModal
